/* aviaf — Ops tab: decision queue, incidents timeline, lane control, fleet/devops, budget history, action journal */
(() => {
const { useState } = React;
const S = () => window.OpsStore;

/* ── decision queue ── */
function DecisionQueue() {
  useOps();
  // A proposed row leaves the queue once a real approval/denial row references
  // it (no transition endpoint in phase 1, so the decision is journalled as its
  // own techops_actions row — the queue reflects that real state, not a fake
  // status flip).
  const decided = new Set(
    S().actions
      .filter((r) => (r.kind === "approval" || r.kind === "denial") && r.args && r.args.decision_pk != null)
      .map((r) => r.args.decision_pk));
  const q = S().actions
    .filter((r) => r.status === "proposed" && !decided.has(r.pk))
    .sort((a, b) => b.ts - a.ts);
  return (
    <>
      <window.SectionHeader title="Decision queue" count={q.length} sub="every proposed techops_actions row — research recs, promotions, budget changes, restarts" />
      {q.length === 0 ? <div className="ops-empty mono">queue empty — nothing awaits your ✔/✘</div> : (
        <div className="dq-grid">{q.map((r) => <window.DecisionCard key={r.pk} r={r} />)}</div>
      )}
    </>
  );
}

/* ── incidents timeline ── */
function Incidents() {
  useOps(); window.useTicker(20000);
  const [f, setF] = useState("all");
  const list = S().incidents.map((i) => window.opsIncidentMeta(i.id)).filter((m) => f === "all" || m.status === "open");
  return (
    <>
      <window.SectionHeader title="Incidents" count={S().incidents.filter((i) => i.status === "open").length + " open"} sub="one incident object, three entry points — same thread on Alerts &amp; Research" right={
        <>{["open", "all"].map((k) => <button key={k} data-active={f === k} onClick={() => setF(k)}>{k}</button>)}</>
      } />
      <div>
        {list.map((m) => (
          <div key={m.id} className="incl-row" onClick={() => window.openIncidentDrawer(m.id)}>
            <span className={"inc-dot " + (m.status === "open" ? (m.openCrit ? "neg" : "warn") : "ok")} />
            <span className="mono incl-id">{m.id}</span>
            <span className="incl-title">{m.title}</span>
            <span className="mono muted incl-meta">
              {m.status === "open" ? <b className="neg">OPEN <window.Age ts={m.opened} /></b> : <span>closed {window.fmtTs(m.closed)}</span>}
              <span> · {m.actionCount} actions · last: {m.lastActor}</span>
            </span>
            {m.stale && <span className="stale-chip">STALE — no action {window.fmtAge(m.lastAct)}</span>}
            <span className="mono muted">→</span>
          </div>
        ))}
        {list.length === 0 && <div className="ops-empty mono">no open incidents</div>}
      </div>
    </>
  );
}

/* ── lane control panel (§5) ── */
function Stepper({ value, fmt, onDelta, title }) {
  return (
    <span className="lstep mono" title={title}>
      <button onClick={() => onDelta(-1)}>−</button>
      <span className="tnum">{fmt(value)}</span>
      <button onClick={() => onDelta(1)}>+</button>
    </span>
  );
}
function LanePanel() {
  useOps(); window.useTicker(30000);
  const [kill, setKill] = useState(null);
  const pol = S().node.policy || {};
  return (
    <div className="lanes-wrap">
      <div className="master-row mono">
        <span className="mr-lbl">global breakers</span>
        <label className="mr-sw"><input type="checkbox" checked={!!pol.cr_enabled} onChange={(e) => S().setMaster("cr_enabled", e.target.checked ? 1 : 0)} /> cr_enabled</label>
        <label className="mr-sw"><input type="checkbox" checked={!!pol.techops_enabled} onChange={(e) => S().setMaster("techops_enabled", e.target.checked ? 1 : 0)} /> techops_enabled</label>
        <span className="muted">sit above lanes — node honors on next policy-sync</span>
        <span style={{ flex: 1 }} />
        <span className="muted">policy updated {window.fmtTs(pol.updated_us)} by {pol.updated_by}</span>
      </div>
      <div className="lane-table">
        <div className="lt-h mono"><span>lane</span><span>trigger</span><span>model</span><span>sessions</span><span>tokens</span><span>spend</span><span>last activity</span><span>switches</span></div>
        {S().lanes.map((l) => {
          const masterDead = (l.id === "techops" ? !pol.techops_enabled : !pol.cr_enabled && l.id !== "ops-review");
          const dead = !l.enabled || l.killed || masterDead;
          return (
            <div key={l.id} className="lt-row" data-dead={dead}>
              <span className="lt-lane mono"><b>{l.t}</b> {l.label}{l.killed && <span className="kill-chip">KILLED</span>}</span>
              <span className="mono muted lt-trig">{l.trigger}</span>
              <span><window.ModelChip model={l.model} note={l.modelNote} />{l.modelNote && <span className="lt-mnote mono">{l.modelNote}</span>}</span>
              <span className="lt-bud"><Stepper value={l.budget.sessions} fmt={(v) => v + "/" + l.budget.sessionsUnit} title="sessions budget — journalled" onDelta={(d) => { const to = Math.max(1, l.budget.sessions + d); S().setLane(l.id, { budget: { sessions: to } }, `sessions budget → ${to}/${l.budget.sessionsUnit}`); }} /></span>
              <span className="lt-bud"><Stepper value={l.budget.tokens} fmt={(v) => window.fmtTok(v) + "/" + l.budget.tokensUnit} title="token budget — journalled" onDelta={(d) => { const to = Math.max(50000, l.budget.tokens + d * 50000); S().setLane(l.id, { budget: { tokens: to } }, `token budget → ${window.fmtTok(to)}/${l.budget.tokensUnit}`); }} /></span>
              <span className="lt-spend">
                <span className="mono muted lt-sp-lbl" title="the node does not report per-lane spend to the hub yet — honest empty state (§3.6)">spend not reported</span>
              </span>
              <span className="mono muted lt-last">{l.lastActivity ? <window.Age ts={l.lastActivity} suffix=" ago" /> : "—"}{l.lastNote && <span className="lt-lnote">{l.lastNote}</span>}</span>
              <span className="lt-sw">
                <button className="osw" data-on={l.enabled && !l.killed} onClick={() => S().setLane(l.id, { top: { enabled: !l.enabled } }, `enable → ${!l.enabled}`)} title="lane enable (journalled)"><span className="osw-knob" /></button>
                {l.killed
                  ? <button className="al-mini on" onClick={() => S().setLane(l.id, { top: { killed: false } }, "kill switch RESET")}>reset kill</button>
                  : <button className="al-mini neg" onClick={() => setKill(l.id)}>KILL</button>}
              </span>
              <span className="lt-may mono">may: {l.may}</span>
            </div>
          );
        })}
      </div>
      <div className="lanes-foot mono">a lane at 100% budget hard-stops (node reports “skipped: budget”) · per-run model overrides: downward free, upward = decision-queue item · dollar figures, when shown, are estimates</div>
      {kill && <window.OpsConfirm title={`Kill lane ${kill}`} sub="independent kill switch — lane stops accepting sessions immediately" requireText={kill}
        body={<div className="ss-prose" style={{ fontSize: 12.5 }}>The lane hard-stops until reset. In-flight sessions finish; nothing new starts. Journalled as policy_change.</div>}
        confirmLabel="Kill lane" onConfirm={() => S().setLane(kill, { top: { killed: true } }, "KILL SWITCH engaged")} onClose={() => setKill(null)} />}
    </div>
  );
}

/* ── fleet / devops strip ── */
function FleetCard({ f }) {
  useOps(); window.useTicker(15000);
  const [logs, setLogs] = useState(false);
  const [logData, setLogData] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const down = f.state === "silent";
  const cmd = (kind, tier) => {
    if (tier === "destructive") { setConfirm(kind); return; }
    S().sendCommand(f.id, kind, f.accounts);        // real command endpoint, journals server-side
  };
  const toggleLogs = async () => {
    const nv = !logs; setLogs(nv);
    if (nv && logData == null) setLogData((await S().fleetLogs(f.id)) || { _err: true });
  };
  return (
    <div className="fleet-card" data-state={f.state}>
      <div className="fc-l1">
        <span className={"led"} />
        <span className="mono fc-id">{f.id}</span>
        <span className="mono muted fc-kind">{f.kind}</span>
        <span style={{ flex: 1 }} />
        <span className="mono muted">{f.sha} · v{f.ver}</span>
      </div>
      {down ? (
        <div className="fc-offline">
          <div className="fo-band mono">AGENT OFFLINE · values as of {window.fmtTs(f.lastSeen)}</div>
          {f.frozen
            ? <div className="fo-frozen mono">equity {window.fmt$(f.frozen.equity, 2)} · day {window.fmt$(f.frozen.dayPnl, 2)} · {f.frozen.positions} positions <span className="muted">(frozen)</span></div>
            : <div className="fo-frozen mono muted">no equity snapshot cached for this instance</div>}
          <div className="fo-reason mono">{f.reason}</div>
        </div>
      ) : (
        <div className="fc-live mono">
          <span>last seen <window.Age ts={f.lastSeen} suffix=" ago" /></span>
          <span>up since {window.fmtTs(f.since)}</span>
          <span>accounts {f.accounts.join(" · ")}</span>
          {f.recovery && <span className="muted">host recovery: {f.recovery}</span>}
        </div>
      )}
      {f.loopLag && (
        <div className="fc-lag">
          <span className="mono muted lbl">loop_lag_max_60s</span>
          <span style={{ color: f.loopLagMax > 250 ? "var(--warn)" : "var(--ink)", flex: 1 }}><window.Sparkline data={f.loopLag} height={22} /></span>
          <span className="mono tnum" style={{ color: f.loopLagMax > 250 ? "var(--warn)" : "var(--muted)" }}>max {f.loopLagMax}ms · last {f.loopLagLast}ms</span>
        </div>
      )}
      <div className="fc-cmds">
        {[["restart", "destructive"], ["pause", "safe"], ["resume", "safe"], ["config_reload", "destructive"]].map(([k, tier]) => {
          const disabled = down || (f.readOnly && k !== "restart");
          const why = down ? "engine unreachable — a dead process cannot receive commands; host-side recovery armed" : f.readOnly ? "read-only monitor (phase 1) — refuses mutating commands" : null;
          return <button key={k} className="fcmd" data-tier={tier} disabled={disabled} title={disabled ? why : `${k} (${tier} tier${tier === "destructive" ? " — typed confirmation" : ""})`} onClick={() => !disabled && cmd(k, tier)}>{k}</button>;
        })}
        <button className="fcmd" onClick={toggleLogs}>{logs ? "hide logs" : "log tail"}</button>
        {down && <span className="mono neg" style={{ fontSize: 10.5 }}>commands disabled — {f.readOnly ? "restart pending operator approval (decision queue)" : "socket dead"}</span>}
      </div>
      {logs && (
        <div className="log-pane" style={{ maxHeight: 180 }}>
          {logData == null ? <div className="log-line mono muted">loading…</div>
           : logData._err ? <div className="log-line mono muted">log tail unavailable (GET /api/instances/{f.id}/logfile returned nothing)</div>
           : (() => {
               const lines = Array.isArray(logData) ? logData
                 : Array.isArray(logData.lines) ? logData.lines
                 : typeof logData.content === "string" ? logData.content.split("\n")
                 : typeof logData.text === "string" ? logData.text.split("\n")
                 : typeof logData === "string" ? logData.split("\n") : [];
               if (!lines.length) return <div className="log-line mono muted">no log lines</div>;
               return lines.slice(-200).map((ln, i) => (
                 <div key={i} className="log-line mono" style={{ gridTemplateColumns: "1fr" }}>
                   <span className="msg">{typeof ln === "string" ? ln : (ln.txt || ln.message || JSON.stringify(ln))}</span>
                 </div>
               ));
             })()}
        </div>
      )}
      {confirm && <window.OpsConfirm title={`${confirm} ${f.id}`} sub="destructive tier — dispatched to the instance socket; a dead engine 503s (§3.7)" requireText={f.id}
        body={<div className="ss-prose" style={{ fontSize: 12.5 }}>Dispatches <b>{confirm}</b> over the live instance socket via the real command endpoint (journalled server-side). If the engine is unreachable the command 503s — host-side recovery is armed.</div>}
        confirmLabel={confirm} onConfirm={() => S().sendCommand(f.id, confirm, f.accounts)} onClose={() => setConfirm(null)} />}
    </div>
  );
}
function Fleet() {
  useOps();
  const nst = S().node.status;
  const npol = S().node.policy || {};
  return <div className="fleet-grid">{S().fleet.map((f) => <FleetCard key={f.id} f={f} />)}
    {S().fleet.length === 0 && <div className="fleet-card"><div className="fc-l1"><span className="mono muted">no instances registered (GET /api/instances)</span></div></div>}
    <div className="fleet-card" data-state="node">
      <div className="fc-l1"><span className="led" /><span className="mono fc-id">aviaf-claude</span><span className="mono muted fc-kind">research/techops node · REST, tick-based</span><span style={{ flex: 1 }} /><span className="mono muted">{nst ? nst.repo_sha : "—"}</span></div>
      <div className="fc-live mono">
        {nst ? <>
          <span>last report <window.Age ts={nst.ts} suffix=" ago" /></span>
          <span>last tick {window.fmtTs(nst.last_tick)} → {nst.last_tick_result}</span>
          <span>sessions today {nst.sessions_today}/{npol.max_sessions_per_day != null ? npol.max_sessions_per_day : "—"}</span>
        </> : <span className="muted">node has not reported status (POST /api/node/status)</span>}
      </div>
      <div className="fc-cmds"><a className="fcmd" href="#/research" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center" }}>node health on Research →</a></div>
    </div>
  </div>;
}

/* ── budget & spend history ── */
function SpendCharts() {
  useOps();
  const days = S().spendHistory;
  if (!days || !days.length) return (
    <div className="spend-grid">
      <div className="spend-card" style={{ gridColumn: "1 / -1" }}>
        <div className="mono muted" style={{ padding: "12px 6px", fontSize: 12, lineHeight: 1.55 }}>
          no per-lane spend history reported yet — the node does not emit per-session/per-lane token usage to the hub.
          This is an honest empty state (§3.6): wire it via node_status.detail_json before charting, never fabricate the bars.
        </div>
      </div>
    </div>
  );
  return (
    <div className="spend-grid">
      {S().lanes.map((l) => {
        const budgetK = l.budget.tokens / 1000;
        const vals = days.map((d) => d[l.id] || 0);
        const max = Math.max(budgetK * 1.15, ...vals);
        const W = 220, Hh = 84, bw = W / days.length;
        const yy = (v) => Hh - (v / max) * (Hh - 14);
        return (
          <div key={l.id} className="spend-card">
            <div className="sp-h mono"><b>{l.label}</b><span className="muted">tokens/day (k) · budget {window.fmtTok(l.budget.tokens)}/{l.budget.tokensUnit}</span></div>
            <svg viewBox={`0 0 ${W} ${Hh}`} className="sp-svg" preserveAspectRatio="none">
              <line x1="0" x2={W} y1={yy(budgetK)} y2={yy(budgetK)} stroke="var(--neg)" strokeWidth="1" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
              {vals.map((v, i) => (
                <rect key={i} x={i * bw + 3} y={yy(v)} width={bw - 6} height={Math.max(0.5, Hh - yy(v))} fill={v > budgetK ? "var(--neg)" : "var(--ink)"} opacity={v ? 0.75 : 0.15} />
              ))}
            </svg>
            <div className="sp-x mono">{days.map((d) => <span key={d.d}>{d.d.slice(3)}</span>)}</div>
            {l.id === "strategic" && <div className="mono muted" style={{ fontSize: 9.5, marginTop: 2 }}>weekly lane — budget line is the per-week cap</div>}
          </div>
        );
      })}
    </div>
  );
}

/* ── action journal (single audit surface, §3.4) ── */
function Journal() {
  useOps(); window.useTicker(20000);
  const [f, setF] = useState("all");
  const [n, setN] = useState(20);
  const rows = S().actions
    .filter((r) => f === "all" ? true : f === "destructive" ? r.tier === "destructive" : f === "policy" ? /policy_change|budget_change|mute/.test(r.kind) : f === "failed" ? (r.status === "failed" || r.flagged) : true)
    .sort((a, b) => b.ts - a.ts);
  return (
    <>
      <window.SectionHeader title="Action journal" count={S().actions.length} sub="techops_actions — if it isn't here, it didn't happen" right={
        <>{["all", "destructive", "policy", "failed"].map((k) => <button key={k} data-active={f === k} onClick={() => setF(k)}>{k}</button>)}</>
      } />
      <div className="jr-table">
        <div className="jr-h mono"><span>ts</span><span>actor</span><span>kind</span><span>tier</span><span>status</span><span>incident</span><span>outcome</span></div>
        {rows.slice(0, n).map((r) => (
          <div key={r.pk} className="jr-row mono" data-flag={r.flagged ? "true" : undefined}>
            <span className="muted">{window.fmtTs(r.ts)}</span>
            <span><window.ActorChip actor={r.actor} /></span>
            <span className="jr-kind">{r.kind}</span>
            <span>{r.tier === "destructive" ? <window.TierChip tier="destructive" /> : <span className="muted">safe</span>}</span>
            <span><window.StatusChip status={r.status} /></span>
            <span>{r.incident ? <a href="#/ops" onClick={(e) => { e.preventDefault(); window.openIncidentDrawer(r.incident); }} className="jr-inc">{r.incident}</a> : <span className="muted">—</span>}</span>
            <span className="jr-out">{r.outcome || (r.args ? JSON.stringify(r.args) : "—")}{r.flagged && <b className="neg"> · FLAGGED</b>}</span>
          </div>
        ))}
      </div>
      {rows.length > n && <button className="al-mini" style={{ margin: "10px var(--pad)" }} onClick={() => setN(n + 30)}>show {Math.min(30, rows.length - n)} more of {rows.length}</button>}
    </>
  );
}

window.PageOps = function PageOps() {
  useOps();
  return (
   <div className="ops-console">
    <div className="ops-page">
      <DecisionQueue />
      <Incidents />
      <window.SectionHeader title="Lane control" sub="who may do what, at which model tier, at what cost — and how to stop it" />
      <LanePanel />
      <window.SectionHeader title="Fleet / devops" sub="command buttons obey the socket — a dead engine cannot receive commands" />
      <Fleet />
      <window.SectionHeader title="Budget &amp; spend history" sub="per-lane token spend by day · budget-line overlays · dollar figures would be estimates" />
      <SpendCharts />
      <Journal />
    </div>
   </div>
  );
};
/* app.jsx routes the Ops tab to window.PageTechops (the prior page name) —
 * alias it so the new design takes over without an app.jsx route rename. */
window.PageTechops = window.PageOps;
})();
